import { useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Bookmark,
    BookmarkCheck,
    BadgeCheck,
    Sparkles,
    MessageCircle,
    Star,
    MapPin,
    Globe,
    Shuffle,
    CreditCard,
    Phone,
    Clock,
    ShieldCheck,
    CheckCircle2,
    Rocket,
    Flag,
} from "lucide-react";
import { useState } from "react";
import { getServiceById, formatServicePrice } from "@/lib/entitiesStore";
import {
    getOrCreateConversation,
    isServiceSaved,
    toggleSaveService,
} from "@/lib/socialStore";
import { addPendingFunds } from "@/lib/monetizationStore";
import { recordView, recordClick, recordBooking } from "@/lib/analyticsStore";
import { useAuth } from "@/context/AuthContext";
import { useSocialVersion } from "@/lib/hooks";
import InitialAvatar from "@/components/InitialAvatar";
import PaymentSheet from "@/components/PaymentSheet";
import BookingSheet from "@/components/BookingSheet";
import ReportSheet from "@/components/ReportSheet";
import MoreMenu from "@/components/MoreMenu";

const ServiceDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { user } = useAuth();
    const v = useSocialVersion();
    const [slide, setSlide] = useState(0);
    const [showPay, setShowPay] = useState(false);
    const [showBook, setShowBook] = useState(false);
    const [paymentDone, setPaymentDone] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);

    const service = useMemo(() => getServiceById(id), [id]);
    // Analytics: record a view once per mount.
    useEffect(() => {
        if (service?.id) recordView("service", service.id);
    }, [service?.id]);
    const saved = useMemo(
        () => (user && service ? isServiceSaved(user.id, service.id) : false),
        [user, service, v]
    );

    if (!service) {
        return (
            <div className="screen items-center justify-center" data-testid="service-not-found">
                <p className="text-[14px] text-muted-foreground">Service not found.</p>
                <button onClick={() => navigate(-1)} className="mt-3 text-primary text-[13px] font-medium">
                    Go back
                </button>
            </div>
        );
    }

    const images = service.images?.length ? service.images : [service.image];
    const isOwner = !!(user && service.ownerId && user.id === service.ownerId);
    // Provider-premium gate for WhatsApp + phone display.
    const providerIsPremium = !!service.premium;
    const showWhatsapp = !isOwner && providerIsPremium && service.whatsappEnabled;
    const showPhone = !isOwner && providerIsPremium && service.phoneEnabled && !!service.providerPhone;

    const serviceType = service.serviceType || "in_person";
    const pricingType = service.pricingType || (service.priceFrom ? "from" : "none");
    const hasPrice = pricingType === "fixed" || pricingType === "from";
    // Owners can't pay/book/chat themselves
    const allowPayment = !isOwner && (serviceType === "online" || serviceType === "hybrid") && hasPrice;
    const allowChat = !isOwner && serviceType !== "online";

    const TYPE_META = {
        in_person: { icon: MapPin,  label: "In-person" },
        online:    { icon: Globe,   label: "Online" },
        hybrid:    { icon: Shuffle, label: "Hybrid" },
    };
    const TypeIcon = TYPE_META[serviceType].icon;

    const startChat = () => {
        if (!user) return;
        const peer = {
            id: service.ownerId || `svc_${service.id}`,
            name: service.providerName || service.name,
            username: service.providerUsername || service.name?.toLowerCase().replace(/\s+/g, "_"),
        };
        const convId = getOrCreateConversation(user.id, peer);
        navigate(`/messages/${convId}`);
    };

    const handlePay = () => {
        // Open the full booking flow (date/time/location) which will open
        // PaymentSheet on proceed. This replaces the direct-payment shortcut.
        if (service?.id) recordClick("service", service.id);
        setShowBook(true);
    };

    const handleBookingSuccess = (booking) => {
        if (service?.id) recordBooking("service", service.id);
        setPaymentDone(true);
        setTimeout(() => {
            setPaymentDone(false);
            if (booking?.id) navigate(`/bookings/${booking.id}`);
        }, 1600);
    };

    const handlePaymentSuccess = () => {
        // Park funds in provider's pending balance (escrow).
        if (service.ownerId) {
            addPendingFunds(service.ownerId, amt, {
                kind: "escrow_in",
                title: `Service booking — ${service.name}`,
                escrowId: `svc_${service.id}_${Date.now()}`,
            });
        }
        setShowPay(false);
        setPaymentDone(true);
        setTimeout(() => setPaymentDone(false), 2600);
    };

    const amt = Number(service.price ?? service.priceFrom ?? 0);

    const formatDays = (days) => {
        if (!days?.length) return "";
        if (days.length === 7) return "Every day";
        return days.join(", ");
    };

    return (
        <div className="screen" data-testid="service-detail-screen">
            <div className="relative aspect-[16/11] bg-muted">
                {(images[slide] || images[0]) ? (
                    <img
                        src={images[slide] || images[0]}
                        alt={service.name}
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                ) : null}
                <div aria-hidden className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0) 35%)" }} />
                <button
                    onClick={() => navigate(-1)}
                    data-testid="service-back-btn"
                    className="absolute top-[max(12px,env(safe-area-inset-top))] left-3 h-10 w-10 flex items-center justify-center rounded-full bg-background/85 backdrop-blur-md border border-border"
                    aria-label="Back"
                >
                    <ArrowLeft size={18} />
                </button>
                <button
                    onClick={() => user && toggleSaveService(user.id, service.id)}
                    data-testid="service-save-btn"
                    className="absolute top-[max(12px,env(safe-area-inset-top))] right-3 h-10 w-10 flex items-center justify-center rounded-full bg-background/85 backdrop-blur-md border border-border"
                    aria-label="Save"
                    aria-pressed={saved}
                >
                    {saved ? <BookmarkCheck size={18} className="text-primary" /> : <Bookmark size={18} />}
                </button>

                {!isOwner && user && (
                    <div
                        className="absolute top-[max(12px,env(safe-area-inset-top))] right-14"
                        data-testid="service-more-menu-wrap"
                    >
                        <MoreMenu
                            target={{ kind: "service", id: service.id, name: service.name }}
                            testId="service-more"
                        />
                    </div>
                )}

                {images.length > 1 && (
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {images.map((_, i) => (
                            <button
                                key={i}
                                data-testid={`service-carousel-dot-${i}`}
                                onClick={() => setSlide(i)}
                                aria-label={`Slide ${i + 1}`}
                                className={["h-1.5 rounded-full transition-all", i === slide ? "w-6 bg-white" : "w-1.5 bg-white/50"].join(" ")}
                            />
                        ))}
                    </div>
                )}
            </div>

            <div className="flex-1 overflow-y-auto no-scrollbar px-5 pt-4 pb-6">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                            <h1 className="font-display text-[20px] font-semibold tracking-tight truncate">
                                {service.name}
                            </h1>
                            {service.verified && (
                                <BadgeCheck size={18} className="text-primary shrink-0" />
                            )}
                        </div>
                        <div className="mt-1 flex items-center gap-3 text-[12.5px] text-muted-foreground">
                            <span>{service.category}</span>
                            {service.rating > 0 && (
                                <span className="inline-flex items-center gap-0.5 text-foreground font-medium">
                                    <Star size={11} className="fill-amber-400 text-amber-400" />
                                    {service.rating}
                                    {service.reviews ? <span className="text-muted-foreground font-normal"> ({service.reviews})</span> : null}
                                </span>
                            )}
                        </div>
                        <div className="mt-1 inline-flex items-center gap-1 text-[12.5px] text-muted-foreground">
                            <MapPin size={12} />
                            {service.location}
                        </div>
                    </div>
                    {service.premium && (
                        <span
                            data-testid="service-premium-badge"
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10.5px] font-semibold text-primary-foreground shadow-card"
                            style={{ background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))" }}
                        >
                            <Sparkles size={11} />
                            Premium
                        </span>
                    )}
                </div>

                {/* Price + service-type chips */}
                <div className="mt-3 flex items-center flex-wrap gap-x-3 gap-y-1.5">
                    <p className="text-[14px]" data-testid="service-price">
                        <span className="font-semibold">{formatServicePrice(service)}</span>
                    </p>
                    <span
                        data-testid="service-type-chip"
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary text-[11px] font-medium text-foreground/80 border border-border"
                    >
                        <TypeIcon size={11} />
                        {TYPE_META[serviceType].label}
                    </span>
                </div>

                {service.description && (
                    <p className="mt-3 text-[13.5px] leading-relaxed text-foreground/85" data-testid="service-description">
                        {service.description}
                    </p>
                )}

                {service.availability?.days?.length > 0 && (
                    <section
                        className="mt-5 p-4 rounded-2xl border border-border bg-card flex items-start gap-3"
                        data-testid="service-availability-section"
                    >
                        <span className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                            <Clock size={16} />
                        </span>
                        <div className="min-w-0 flex-1">
                            <h3 className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium">
                                Availability
                            </h3>
                            <p className="mt-1 text-[13.5px] font-medium">
                                {formatDays(service.availability.days)}
                            </p>
                            <p className="text-[12px] text-muted-foreground">
                                {service.availability.from} – {service.availability.to}
                            </p>
                        </div>
                    </section>
                )}

                <section className="mt-5 p-4 rounded-2xl border border-border bg-card" data-testid="service-provider-section">
                    <h3 className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-2">
                        Provider
                    </h3>
                    <div className="flex items-center gap-3">
                        {service.providerAvatar ? (
                            <img src={service.providerAvatar} alt="" className="h-12 w-12 rounded-full object-cover" />
                        ) : (
                            <InitialAvatar name={service.providerName || service.name} size={48} />
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="font-display text-[14px] font-semibold truncate">
                                {service.providerName || service.name}
                            </div>
                            {service.providerUsername && (
                                <div className="text-[12px] text-muted-foreground">@{service.providerUsername}</div>
                            )}
                        </div>
                        {service.rating > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[12.5px] font-medium">
                                <Star size={11} className="fill-amber-400 text-amber-400" />
                                {service.rating}
                            </span>
                        )}
                    </div>
                </section>

                {/* Contact & payment actions — depend on serviceType + premium */}
                <section className="mt-5 space-y-2.5" data-testid="service-contact-section">
                    {isOwner && (
                        <button
                            data-testid="service-owner-boost-btn"
                            onClick={() => navigate("/boost")}
                            className="w-full h-12 rounded-2xl text-primary-foreground inline-flex items-center justify-center gap-2 text-[13.5px] font-medium active:scale-[0.98] transition-transform"
                            style={{
                                background:
                                    "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                                boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                            }}
                        >
                            <Rocket size={16} />
                            {user?.premium ? "Boost this service" : "Boost — Premium"}
                        </button>
                    )}
                    {allowPayment && (
                        <>
                            <button
                                data-testid="service-pay-btn"
                                onClick={handlePay}
                                className="w-full h-12 rounded-2xl text-primary-foreground inline-flex items-center justify-center gap-2 text-[13.5px] font-medium active:scale-[0.98] transition-transform"
                                style={{
                                    background:
                                        "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                                    boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                                }}
                            >
                                <CreditCard size={16} />
                                {pricingType === "fixed"
                                    ? `Book Now · ${formatServicePrice(service)}`
                                    : `Book Now · from ${formatServicePrice(service)}`}
                            </button>
                            <p
                                data-testid="service-escrow-note"
                                className="text-center text-[11.5px] text-muted-foreground inline-flex items-center justify-center gap-1.5 w-full"
                            >
                                <ShieldCheck size={11} className="text-primary" />
                                Secure payment • Escrow protected
                            </p>
                        </>
                    )}

                    {(allowChat || showWhatsapp) && (
                        <div
                            className={
                                allowChat && showWhatsapp ? "grid grid-cols-2 gap-2" : "grid grid-cols-1"
                            }
                        >
                            {allowChat && (
                                <button
                                    data-testid="service-contact-chat"
                                    onClick={startChat}
                                    className={[
                                        "h-12 rounded-2xl inline-flex items-center justify-center gap-2 text-[13.5px] font-medium active:scale-[0.98] transition-transform",
                                        allowPayment
                                            ? "bg-secondary text-foreground border border-border hover:bg-muted"
                                            : "text-primary-foreground",
                                    ].join(" ")}
                                    style={
                                        allowPayment
                                            ? undefined
                                            : {
                                                  background:
                                                      "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                                                  boxShadow:
                                                      "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                                              }
                                    }
                                >
                                    <MessageCircle size={16} />
                                    Chat
                                </button>
                            )}

                            {showWhatsapp && (
                                <a
                                    href={
                                        service.providerPhone
                                            ? `https://wa.me/${service.providerPhone.replace(/\D/g, "")}`
                                            : "#"
                                    }
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="service-contact-whatsapp"
                                    className="h-12 rounded-2xl bg-emerald-500 text-white inline-flex items-center justify-center gap-2 text-[13.5px] font-medium active:scale-[0.98] transition-transform"
                                >
                                    <svg viewBox="0 0 32 32" width="16" height="16" fill="currentColor" aria-hidden>
                                        <path d="M19.1 17.2c-.3-.2-1.7-.9-2-1s-.5-.2-.7.2-.8 1-1 1.2-.4.2-.7 0c-.3-.2-1.3-.5-2.5-1.6-.9-.8-1.5-1.8-1.7-2.1s0-.5.1-.6c.1-.1.3-.4.5-.6s.2-.3.3-.5c.1-.2 0-.4 0-.5l-1-2.3c-.2-.5-.5-.4-.7-.5H9c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 3 1.2 3.2c.2.2 2.2 3.3 5.3 4.6 3.1 1.3 3.1.9 3.7.9.6 0 1.7-.7 2-1.3.2-.7.2-1.2.2-1.3-.1 0-.3-.1-.5-.2zM16 3C8.8 3 3 8.8 3 16c0 2.4.7 4.6 1.8 6.6L3 29l6.6-1.8c1.9 1 4 1.7 6.4 1.7 7.2 0 13-5.8 13-13S23.2 3 16 3zm0 23.8c-2.1 0-4.1-.6-5.8-1.6l-.4-.3-4.1 1.1 1.1-4-.3-.5C5.6 20 5 18 5 16c0-6.1 4.9-11 11-11s11 4.9 11 11-4.9 11-11 11z" />
                                    </svg>
                                    WhatsApp
                                </a>
                            )}
                        </div>
                    )}

                    {showPhone && (
                        <a
                            href={`tel:${service.providerPhone}`}
                            data-testid="service-contact-phone"
                            className="h-12 rounded-2xl bg-secondary border border-border text-foreground inline-flex items-center justify-center gap-2 text-[13.5px] font-medium active:scale-[0.98] transition-transform hover:bg-muted"
                        >
                            <Phone size={15} />
                            {service.providerPhone}
                        </a>
                    )}
                </section>
            </div>

            {/* Booking flow (date/time/location) → hands off to PaymentSheet */}
            <BookingSheet
                open={showBook}
                onClose={() => setShowBook(false)}
                service={service}
                onBooked={handleBookingSuccess}
            />

            {/* Standalone payment sheet retained for legacy/direct-pay paths */}
            <PaymentSheet
                open={showPay}
                onClose={() => setShowPay(false)}
                onSuccess={handlePaymentSuccess}
                amount={amt}
                title={pricingType === "fixed" ? "Confirm payment" : "Confirm booking"}
                purposeLabel="service booking"
                summary={
                    <div className="flex items-center gap-3 p-3 rounded-2xl bg-muted/40 border border-border">
                        {service.image ? (
                            <img src={service.image} alt="" className="h-12 w-12 rounded-xl object-cover shrink-0" />
                        ) : (
                            <div className="h-12 w-12 rounded-xl overflow-hidden shrink-0">
                                <InitialAvatar name={service.name} size={48} />
                            </div>
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="font-display text-[14px] font-semibold truncate">
                                {service.name}
                            </div>
                            <div className="text-[11.5px] text-muted-foreground truncate">
                                {service.category} · {TYPE_META[serviceType].label}
                            </div>
                        </div>
                    </div>
                }
                refundNote={
                    <p className="text-center text-[11.5px] text-muted-foreground inline-flex items-center justify-center gap-1.5 w-full">
                        <ShieldCheck size={11} className="text-primary" />
                        Funds held in escrow until completion
                    </p>
                }
            />

            {/* Success toast */}
            {paymentDone && (
                <div
                    data-testid="service-pay-success"
                    role="status"
                    className="absolute left-1/2 -translate-x-1/2 top-[calc(env(safe-area-inset-top)+12px)] z-50 px-4 py-2.5 rounded-full bg-card border border-emerald-500/30 shadow-card text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-2 text-[13px] font-medium animate-fade-up"
                >
                    <CheckCircle2 size={16} />
                    {pricingType === "fixed"
                        ? "Booking created — held in escrow"
                        : "Booking sent — provider notified"}
                </div>
            )}

            <ReportSheet
                open={reportOpen}
                onClose={() => setReportOpen(false)}
                target={{ kind: "service", id: service.id, label: service.name }}
            />
        </div>
    );
};

export default ServiceDetail;
